import { useEffect } from 'react'
import { AxiosError } from 'axios'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useDispatch } from '@tabeeb/store'
import { onAddErrorNotification, onAddSuccessNotification } from '@tabeeb/modules/notification/actions'
import {
  ApiErrorResponse,
  CreateFormFolderRequest,
  FormFolderRequest,
  RenameFormFolderRequest,
} from '@tabeeb/contracts/main-api/swagger-main-api'
import getErrorMessageFromResponse from '@tabeeb/modules/shared/utils/getErrorMessageFromResponse'
import { ErrorCode } from '@tabeeb/enums'
import { useApi } from '@tabeeb/modules/shared/utils/API'

import { FormsQueryKeys } from '../queries'
import { FormFolder } from '../types'

const useFormFolders = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()

  const { mainApi: api } = useApi()

  const query = useQuery({
    queryKey: [FormsQueryKeys.FormFolders],
    queryFn: () => api.formFolders.getAllAsync(),
    enabled,
  })

  const { isError, error: queryError } = query

  useEffect(() => {
    if (isError && queryError) {
      const error = queryError as AxiosError<ApiErrorResponse>
      const message = getErrorMessageFromResponse(error.response?.data)
      dispatch(onAddErrorNotification({ message }))
    }
  }, [isError, queryError, dispatch])

  const createMutation = useMutation({
    mutationFn: (data: CreateFormFolderRequest) => api.formFolders.postAsync(data),
    onError: (error: AxiosError<ApiErrorResponse>) => {
      const errorMessage = getErrorMessage(error.response?.data?.Error?.Code)
      dispatch(onAddErrorNotification({ message: errorMessage }))
    },
    onSuccess: () => {
      dispatch(onAddSuccessNotification({ message: 'Folder created successfully' }))
      query.refetch()
    },
  })

  const renameMutation = useMutation({
    mutationFn: (data: RenameFormFolderRequest) => api.formFolders.renameAsync(data),
    onError: () => {
      dispatch(onAddErrorNotification({ message: 'Failed to rename the folder' }))
    },
    onSuccess: () => {
      dispatch(onAddSuccessNotification({ message: 'Folder renamed successfully' }))
      query.refetch()
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (data: FormFolder) => api.formFolders.deleteAsync(data.FolderId),
    onError: () => {
      dispatch(onAddErrorNotification({ message: 'Failed to delete the folder' }))
    },
    onSuccess: () => {
      dispatch(onAddSuccessNotification({ message: 'Folder deleted successfully' }))
      query.refetch()
    },
  })

  const switchMutation = useMutation({
    mutationFn: (data: FormFolderRequest) => api.formFolders.moveFormToFolderAsync(data),
    onError: () => {
      dispatch(onAddErrorNotification({ message: 'Failed to switch the folder' }))
    },
    onSuccess: () => {
      dispatch(onAddSuccessNotification({ message: 'Folder switched successfully' }))
      queryClient.invalidateQueries({ queryKey: [FormsQueryKeys.Forms] })
    },
  })

  const publishMutation = useMutation({
    mutationFn: (data: { folderId: number; tenantsIds: number[] }) =>
      api.formFolders.publishAsync({ FolderId: data.folderId, TenantsIds: data.tenantsIds }),
    onError: () => {
      dispatch(onAddErrorNotification({ message: 'Failed to publish the folder' }))
    },
    onSuccess: () => {
      dispatch(onAddSuccessNotification({ message: 'Folder published successfully' }))
      query.refetch()
    },
  })

  const unPublishMutation = useMutation({
    mutationFn: (data: { folderId: number; tenantsIds: number[] }) =>
      api.formFolders.unpublishAsync({ FolderId: data.folderId, TenantsIds: data.tenantsIds }),
    onError: () => {
      dispatch(onAddErrorNotification({ message: 'Failed to unpublish the folder' }))
    },
    onSuccess: () => {
      dispatch(onAddSuccessNotification({ message: 'Folder unpublished successfully' }))
      query.refetch()
    },
  })

  function getErrorMessage(errorCode: number | undefined): string {
    switch (errorCode) {
      case ErrorCode.ModelInvalid:
        return 'Folder name cannot be empty or exceed 400 characters.'
      default:
        return 'Failed to create a folder.'
    }
  }

  return {
    loading: query.isLoading,
    formFolders: query.data,
    onCreate: createMutation.mutate,
    onRename: renameMutation.mutate,
    onDelete: deleteMutation.mutate,
    onSwitch: switchMutation.mutate,
    onPublish: publishMutation.mutate,
    onUnpublish: unPublishMutation.mutate,
    onReload: query.refetch,
  }
}

export default useFormFolders
